import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, GitCompare, Check } from "lucide-react";
import { fmtPrice } from "../lib/api";
import { handleImgError } from "../lib/imgFallback";
import { useCart } from "../context/CartContext";
import { useCompare } from "../context/CompareContext";
import QuickViewModal from "./QuickViewModal";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const { toggle, has } = useCompare();
  const [qv, setQv] = useState(false);
  const inCompare = has(product.slug);
  const low = typeof product.stock === "number" && product.stock <= 10;

  const add = (e) => {
    e.preventDefault();
    addItem(product, 1);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, delay: (index % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
        data-testid={`product-card-${product.slug}`}
        className="group relative flex flex-col bg-[#1F1F1F] border border-white/[0.06] overflow-hidden transition-[border-color] duration-500 hover:border-[#4ADE80]/40"
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-[#0F0F10]">
          <Link to={`/products/${product.slug}`} className="block h-full w-full">
            <img
              src={product.image}
              alt={product.name}
              loading="lazy"
              onError={handleImgError}
              className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04] group-hover:rotate-[1.5deg]"
            />
          </Link>
          <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] text-[#A8A8A8]">{product.category}</span>
          {low && (
            <span data-testid={`stock-${product.slug}`} className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.14em] text-[#4ADE80]">
              Only {product.stock} left
            </span>
          )}
          {/* hover actions */}
          <div className="absolute bottom-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <button data-testid={`quickview-${product.slug}`} onClick={() => setQv(true)} aria-label="Quick view"
              className="btn-press h-9 w-9 flex items-center justify-center bg-[#0F0F10]/80 backdrop-blur border border-white/15 text-[#F5F5F5] hover:border-[#4ADE80] hover:text-[#4ADE80]"><Eye className="h-4 w-4" /></button>
            <button data-testid={`compare-toggle-${product.slug}`} onClick={() => toggle(product)} aria-label="Add to compare"
              className={`btn-press h-9 w-9 flex items-center justify-center backdrop-blur border ${inCompare ? "bg-[#4ADE80] text-[#0F0F10] border-[#4ADE80]" : "bg-[#0F0F10]/80 border-white/15 text-[#F5F5F5] hover:border-[#4ADE80] hover:text-[#4ADE80]"}`}>
              {inCompare ? <Check className="h-4 w-4" /> : <GitCompare className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-7 flex flex-col flex-1">
          <Link to={`/products/${product.slug}`}>
            <h3 className="font-display text-base sm:text-lg tracking-tight text-[#F5F5F5] group-hover:text-[#4ADE80] transition-colors duration-300 line-clamp-2">{product.name}</h3>
          </Link>
          <p className="mt-1 text-[11px] sm:text-[12px] text-[#A8A8A8] font-light">{product.size}</p>
          <div className="mt-4 sm:mt-6 flex items-end justify-between gap-2">
            <span className="font-display text-base sm:text-lg text-[#F5F5F5] shrink-0">{fmtPrice(product.price)}</span>
            <button
              data-testid={`add-to-cart-${product.slug}`}
              onClick={add}
              className="btn-press text-[10px] sm:text-[11px] uppercase tracking-[0.14em] sm:tracking-[0.18em] text-[#0F0F10] bg-[#4ADE80] px-3 sm:px-5 py-2 sm:py-2.5 hover:bg-[#6FE998] shrink-0"
            >
              Add
            </button>
          </div>
        </div>
      </motion.div>
      <QuickViewModal product={product} open={qv} onOpenChange={setQv} />
    </>
  );
}
